/**
 * Shops and services (TD §7.1 step 9), ported from the prototype's placeFacilities: restaurant, gift shop and
 * info point in the plaza, then toilets and kiosks (and a restaurant halfway) spread along the walking route.
 */
import type { IconType, PlanDoc, Pt } from '../../shared/types';
import { bbox, distPoly, pip, riverPoly } from '../geom';
import { pointAtRoute, type Rng } from '../raster';
import { areaRing, plotRing, walkingRoutes, type IdMaker } from './fill';

/** How many shops and services a generated zoo of this size gets (the prototype's default was 9 at 400 m). */
export function shopCount(size: number): number {
  return Math.max(4, Math.min(14, Math.round(size / 45)));
}

/** Places `n` shop icons; returns the types that found no spot. */
export function placeFacilities(doc: PlanDoc, r: Rng, n: number, ids: IdMaker): IconType[] {
  const plot = plotRing(doc), fails: IconType[] = [];
  const zones = Object.keys(doc.areas).map(id => {
    const a = doc.areas[id], ring = areaRing(doc, id);
    return { kind: a?.kind ?? 'plaza', ring, bb: bbox(ring) };
  });
  const routes = walkingRoutes(doc);
  const allRoutes = walkingRoutesAll(doc);
  const rivers = Object.values(doc.rivers).map(rv => ({ poly: riverPoly(rv.pts), w: rv.w }));
  const add = (type: IconType, x: number, y: number) => { doc.icons[ids(doc.icons, 'i')] = { type, x, y }; };
  const tooClose = (x: number, y: number, d: number) => Object.values(doc.icons).some(f => Math.hypot(f.x - x, f.y - y) < d);
  const onRoad = (x: number, y: number) => allRoutes.some(rt => distPoly(rt.poly, x, y, false) < rt.w / 2 + 3);

  const freeSpot = (x: number, y: number, padR: number) => {
    if (plot && (!pip(plot, x, y) || distPoly(plot, x, y, true) < padR)) return false;
    const blocks = (z: (typeof zones)[number]) => {
      if (z.kind === 'plaza' || z.ring.length < 3 || x < z.bb[0] - padR || x > z.bb[2] + padR || y < z.bb[1] - padR || y > z.bb[3] + padR) return false;
      const inside = pip(z.ring, x, y), d = distPoly(z.ring, x, y, true);
      return z.kind === 'habitat' || z.kind === 'aviary' || z.kind === 'aquarium' ? inside && d > 7 : inside || d < padR * 0.6;
    };
    if (zones.some(blocks)) return false;
    if (rivers.some(rv => distPoly(rv.poly, x, y, false) < rv.w / 2 + padR)) return false;
    if (onRoad(x, y)) return false;
    return !tooClose(x, y, 18);
  };

  const plaza = zones.find(z => z.kind === 'plaza');
  const plazaTypes = (['restaurant', 'gift', 'info'] as IconType[]).slice(0, Math.min(3, n));
  for (const type of plazaTypes) {
    let ok = false;
    if (plaza) {
      const [x0, y0, x1, y1] = plaza.bb;
      for (let t = 0; t < 150 && !ok; t++) {
        const x = x0 + r() * (x1 - x0), y = y0 + r() * (y1 - y0);
        if (pip(plaza.ring, x, y) && distPoly(plaza.ring, x, y, true) > 5 && !onRoad(x, y) && !tooClose(x, y, 14)) { add(type, x, y); ok = true; }
      }
    }
    if (!ok) fails.push(type);
  }
  const along = Math.max(0, n - plazaTypes.length), total = routes.reduce((s, rt) => s + rt.len, 0);
  if (!total) return fails.concat(Array.from({ length: along }, (_, i) => (i % 2 ? 'kiosk' : 'toilets') as IconType));
  const types: IconType[] = Array.from({ length: along }, (_, i) => (i % 2 ? 'kiosk' : 'toilets'));
  if (along >= 3) types[Math.floor(along / 2)] = 'restaurant';
  types.forEach((type, i) => {
    const W = (i + 0.5) / along * total;
    let done = false;
    for (const dW of [0, 10, -10, 22, -22, 36, -36, 55, -55, 80, -80]) {
      let w = ((W + dW) % total + total) % total, ri = 0;
      while (ri < routes.length - 1 && w > (routes[ri]?.len ?? 0)) { w -= routes[ri]?.len ?? 0; ri++; }
      const rt = routes[ri];
      if (!rt) break;
      const q = pointAtRoute(rt, w), sides = r() < 0.5 ? [1, -1] : [-1, 1];
      for (const sd of sides) for (const off of [rt.w / 2 + 6, rt.w / 2 + 9, rt.w / 2 + 13]) {
        const x = q.x + q.nx * sd * off, y = q.y + q.ny * sd * off;
        if (!done && freeSpot(x, y, 6)) { add(type, x, y); done = true; }
      }
      if (done) break;
    }
    if (!done) fails.push(type);
  });
  return fails;
}

/** Every road's centre line, including entrance spurs (for keeping icons off the road). */
function walkingRoutesAll(doc: PlanDoc): { poly: Pt[]; w: number }[] {
  return walkingRoutes(doc).map(rt => ({ poly: rt.poly, w: rt.w })).concat(
    Object.values(doc.roads).filter(r => r.kind === 'entrance').map(r => {
      const nodes = r.nodes.flatMap(id => { const nd = doc.nodes[id]; return nd ? [nd] : []; });
      return { poly: nodes.map(nd => [nd.x, nd.y] as Pt), w: Math.max(...nodes.map(nd => nd.w)) };
    }),
  );
}
